import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Admin } from '@/types';
import { apiGet, apiPost } from '@/services/base';
import { extractErrorMessage } from '@/services/errorHandler';
import { extractUserInfo } from '@/services/jwt';
import { isConsoleRole } from '@/constants/common';
import { deleteCookie, setCookie } from '@/utils/cookies';

/**
 * The signed-in admin. SUPER_ADMIN only: any other role gets a plain
 * sentence and no session, matching the server, which 403s every feed the
 * console reads for anyone else.
 */
interface AuthState {
  admin: Admin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string, twoFactorCode?: string) => Promise<void>;
  /** Finish a sign-in whose token arrived some other way (Google). */
  acceptToken: (accessToken: string, refreshToken?: string | null) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export const NOT_AN_ADMIN = 'That account is not an admin of admin.posture.vision.';

const roleOf = (accessToken: string): string | null => {
  const info = extractUserInfo(accessToken);
  const role = info && typeof info === 'object' ? (info as Record<string, unknown>).role : null;
  return typeof role === 'string' ? role : null;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      admin: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      acceptToken: async (accessToken, refreshToken) => {
        if (!isConsoleRole(roleOf(accessToken))) {
          set({ admin: null, isAuthenticated: false, isLoading: false, error: NOT_AN_ADMIN });
          throw new Error(NOT_AN_ADMIN);
        }
        localStorage.setItem('token', accessToken);
        if (refreshToken) {
          setCookie('refresh_token', String(refreshToken), { maxAge: 7 * 24 * 60 * 60, sameSite: 'Lax', path: '/' });
        }
        const me = (await apiGet('/admin/me', true)) as Admin;
        set({ admin: me, isAuthenticated: true, isLoading: false, error: null });
      },

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const res = (await apiPost('/auth/admin/login', { email, password })) as Record<string, unknown>;
          const accessToken = (res.accessToken ?? res.access_token) as string | undefined;
          const refreshToken = (res.refreshToken ?? res.refresh_token) as string | undefined;
          if (!accessToken) throw new Error('Invalid email or password');
          await get().acceptToken(accessToken, refreshToken ?? null);
        } catch (err) {
          set({ isLoading: false, error: extractErrorMessage(err) });
          throw err;
        }
      },

      logout: () => {
        localStorage.removeItem('token');
        deleteCookie('refresh_token', { path: '/' });
        set({ admin: null, isAuthenticated: false, error: null });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'pv-admin-auth',
      partialize: (state) => ({ admin: state.admin, isAuthenticated: state.isAuthenticated }),
    },
  ),
);
